<template>
  <div class="visual-report-visualization">

    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
      <div class="d-flex align-items-center gap-2">
        <button class="btn btn-sm btn-outline-secondary" @click="goBackToList" title="Wróć do listy">
          <i class="fas fa-arrow-left me-1"></i>Wróć
        </button>
        <div v-if="viewConfig">
          <h5 class="mb-0 fw-bold">
            <i class="fas fa-chart-bar me-2 text-primary"></i>
            {{ viewConfig.reportName }}
          </h5>
          <small class="text-muted" v-if="viewConfig.reportDescription">{{ viewConfig.reportDescription }}</small>
        </div>
        <div v-else-if="loadingConfig" class="text-muted small">
          <i class="fas fa-spinner fa-spin me-1"></i>Ładowanie raportu...
        </div>
      </div>

      <div class="d-flex gap-2 flex-wrap">
  <span v-if="viewConfig" class="badge bg-light text-secondary border fw-normal px-2 py-1 align-self-center">
    <i class="fas fa-tag me-1"></i>{{ reportMetadata != null ? reportMetadata.reportTypeName : '' }}
  </span>

        <button
            class="btn btn-sm btn-outline-primary"
            @click="reloadData"
            :disabled="loadingRows"
            title="Odśwież dane"
        >
          <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': loadingRows }"></i>Odśwież
        </button>

        <button
            v-if="currentChart"
            class="btn btn-sm btn-outline-secondary"
            @click="toggleChartVisibility"
            :title="isChartPanelVisible ? 'Ukryj wykres' : 'Pokaż wykres'"
        >
          <i :class="isChartPanelVisible ? 'fas fa-eye-slash' : 'fas fa-eye'" class="me-1"></i>
          {{ isChartPanelVisible ? 'Ukryj wykres' : 'Pokaż wykres' }}
        </button>
        <button
            class="btn btn-sm btn-outline-secondary"
            @click="toggleTableVisibility"
            :title="isTableVisible ? 'Ukryj tabelę' : 'Pokaż tabelę'"
        >
          <i class="fas me-1" :class="isTableVisible ? 'fa-eye-slash' : 'fa-eye'"></i>
          {{ isTableVisible ? 'Ukryj tabelę' : 'Pokaż tabelę' }}
        </button>

        <button
            class="btn btn-sm btn-secondary"
            @click="openSettingsModal"
            title="Ustawienia widoku raportu"
        >
          <i class="fas fa-cog me-1"></i>Ustawienia
        </button>
      </div>


    </div>

    <!-- Loading config indicator -->
    <div v-if="loadingConfig" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
      <p class="mt-2 text-muted">Ładowanie konfiguracji raportu...</p>
    </div>

    <div v-else-if="viewConfig">

      <!-- Search bar -->
      <SearchBar2
          v-if="searchableFields && searchableFields.length > 0"
          :searchOptions="searchableFields"
          @searchRequest="onSearchRequest($event)"
      />

      <!-- Filters section -->
      <CommonTableFilterSection2
          :filtersFetchMethod="filtersFetchMethod"
          :filtersFetchParams="{}"
          :filtersSet="selectedFilters"
          @onSelectedFilters="onFiltersChanged($event)"
      >
        <template #filter-additional-buttons>
          <span v-if="savedFiltersApplied" class="badge bg-success align-self-center px-2 py-1 small">
            <i class="fas fa-check me-1"></i>Filtry zapisane
          </span>
        </template>
      </CommonTableFilterSection2>

      <!-- Aggregation panel -->
      <div v-if="hasAggregationMetadata" class="card shadow-sm mt-3 mb-2">
        <div class="card-header bg-light d-flex align-items-center justify-content-between">
          <h6 class="mb-0 text-primary fw-bold">
            <i class="fas fa-layer-group me-2"></i>Grupowanie danych
          </h6>
          <div class="d-flex gap-2 align-items-center">
            <span v-if="aggregationEnabled" class="badge bg-primary small">Tryb agregacji aktywny</span>
            <button
                v-if="!aggregationEnabled"
                class="btn btn-sm btn-outline-primary"
                @click="enableAggregation"
            >
              <i class="fas fa-play me-1"></i>Pokaż panel grupowania
            </button>
            <button
                v-else
                class="btn btn-sm btn-outline-secondary"
                @click="disableAggregation"
            >
              <i class="fas fa-stop me-1"></i>Zamknij panel grupowania
            </button>
          </div>
        </div>
        <div v-if="aggregationEnabled" class="card-body">
          <div class="row g-3">

            <!-- Group-by columns -->
            <div class="col-md-5">
              <label class="form-label fw-semibold small">Grupuj według</label>
              <div class="border rounded p-2" style="max-height: 160px; overflow-y: auto;">
                <div
                    v-for="col in aggregationMetadata.groupableColumns"
                    :key="col.key"
                    class="form-check"
                >
                  <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'grp-' + col.key"
                      :value="col.key"
                      v-model="selectedGroupByKeys"
                      @click="saveGroupedColumns"
                  />
                  <label class="form-check-label small" :for="'grp-' + col.key">
                    {{ col.label || col.key }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Measures -->
            <div class="col-md-7">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <label class="form-label fw-semibold small mb-0">Miary</label>
                <button class="btn btn-sm btn-outline-primary py-0 px-2" @click="addMeasure">
                  <i class="fas fa-plus me-1"></i>Dodaj miarę
                </button>
              </div>
              <div v-if="selectedMeasures.length === 0" class="text-muted small py-2">
                Brak wybranych miar. Kliknij „Dodaj miarę".
              </div>
              <div
                  v-for="(measure, idx) in selectedMeasures"
                  :key="idx"
                  class="d-flex align-items-center gap-2 mb-2"
              >
                <select
                    class="form-select form-select-sm"
                    v-model="measure.columnKey"
                    @change="onMeasureKeyChange(idx)"
                    style="min-width: 130px;"
                >
                  <option
                      v-for="spec in aggregationMetadata.measures"
                      :key="spec.key"
                      :value="spec.key"
                  >{{ spec.label || spec.key }}
                  </option>
                </select>
                <select
                    class="form-select form-select-sm"
                    v-model="measure.function"
                    style="min-width: 90px;"
                    @change="onMeasureFunctionChange"
                >
                  <option
                      v-for="fn in (aggregationMetadata.measures.find(s => s.key === measure.columnKey) || {}).allowedFunctions || []"
                      :key="fn"
                      :value="fn"
                  >{{ fn }}
                  </option>
                </select>
<!--                <input-->
<!--                    type="text"-->
<!--                    class="form-control form-control-sm"-->
<!--                    v-model="measure.alias"-->
<!--                    placeholder="Alias"-->
<!--                    style="min-width: 90px;"-->
<!--                />-->
                <button class="btn btn-sm btn-outline-danger py-0 px-2" @click="removeMeasure(idx)">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <!-- Post-aggregation (HAVING) filters for havingFilterable measures -->
            <div
                v-if="selectedMeasures.some(m => m.havingFilterable)"
                class="col-12"
            >
              <label class="form-label fw-semibold small">Filtry po agregacji (HAVING)</label>
              <div class="text-muted small">
                Filtry HAVING są obsługiwane wewnętrznie i wysyłane razem z zapytaniem agregacji.
                Rozbudowany interfejs HAVING można dodać w przyszłości.
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Chart section (above the table) -->
      <!-- Chart section (above the table) -->
      <div class="mt-3 mb-2">
        <!-- Empty state: no chart configured yet -->
        <div v-if="!currentChart" class="card shadow-sm">
          <div class="card-body text-center py-4">
            <i class="fas fa-chart-bar fa-2x text-muted mb-2 d-block"></i>
            <p class="text-muted mb-3 small">Brak wykresu dla tego raportu. Możesz dodać wykres powyżej tabeli
              danych.</p>
            <button class="btn btn-sm btn-outline-primary" @click="openCreateChartModal">
              <i class="fas fa-plus me-1"></i>Utwórz wykres
            </button>
          </div>
        </div>

        <!-- Full chart panel visible only when chart is visible -->
        <div v-else-if="isChartPanelVisible">
          <div class="d-flex align-items-start justify-content-between mb-2">
  <span class="text-muted small mt-1">
    <i class="fas fa-chart-bar me-1"></i>Wykres raportu
  </span>

            <div class="d-flex flex-column align-items-end">
              <div
                  v-if="chartTotalPages > 1"
                  class="d-flex align-items-center gap-1 mb-2"
              >
                <button
                    class="btn btn-sm btn-outline-secondary py-0 px-2"
                    :disabled="!chartHasPrevious"
                    @click="changeChartPage(chartPageNumber - 1)"
                >
                  ‹
                </button>

                <button
                    v-for="page in getChartPageNumbers()"
                    :key="'chart-page-' + page"
                    class="btn btn-sm py-0 px-2"
                    :class="page === chartPageNumber ? 'btn-primary' : 'btn-outline-secondary'"
                    @click="changeChartPage(page)"
                >
                  {{ page + 1 }}
                </button>

                <button
                    class="btn btn-sm btn-outline-secondary py-0 px-2"
                    :disabled="!chartHasNext"
                    @click="changeChartPage(chartPageNumber + 1)"
                >
                  ›
                </button>
              </div>

              <div class="d-flex gap-2">
                <button
                    class="btn btn-sm btn-outline-secondary"
                    @click="refreshChart"
                    :disabled="loadingChart"
                    title="Odśwież dane wykresu"
                >
                  <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': loadingChart }"></i>Odśwież wykres
                </button>
                <button
                    class="btn btn-sm btn-outline-primary"
                    @click="openEditChartModal"
                    title="Edytuj wykres"
                >
                  <i class="fas fa-pencil-alt me-1"></i>Edytuj wykres
                </button>
                <button
                    class="btn btn-sm btn-outline-danger"
                    @click="deleteChart"
                    title="Usuń wykres"
                >
                  <i class="fas fa-trash me-1"></i>Usuń wykres
                </button>
              </div>
            </div>
          </div>

          <VisualReportChartPanel
              :chart="currentChart"
              :chartData="currentChartData"
              :loading="loadingChart"
          />
        </div>
      </div>


      <!-- Data table -->
      <div v-if="isTableVisible" class="mt-2">
        <CommonTable2
            ref="visualReportTable"
            :table="tableData"
            rowIdentifier="__vr_row_idx"
            @onSort="sort($event)"
            :key="tableData.data"
            :currentPage="currentPage"
            :totalRows="visualReportsModule.getVisualReportRowsTotal"
            :totalPages="visualReportsModule.getVisualReportRowsPagesCount"
            :showPaginationTop="true"
            :showPaginationBottom="true"
            :multiselect="true"
            @onPageChange="currentPage = $event"
            :sortColumn="sortingKey"
            :sortDirection="sortingDirection"
            :showMassActions="true"
            :showRowActions="false"
            :showCheckboxColumn="true"
            :stickyScrollbarBottom="1"
            :exportEnabled="true"
            :perPage="pageSize"
            @onVisibleColumnsChange="saveVisibleColumns"
            :initiallyVisibleColumns="initiallyVisibleColumns"
        />
      </div>

    </div>

    <div v-else class="alert alert-warning">
      <i class="fas fa-exclamation-triangle me-2"></i>Nie udało się załadować konfiguracji raportu.
    </div>

    <!-- Settings Modal -->
    <b-modal
        v-model="showSettingsModal"
        id="visual-report-settings-modal"
        scrollable
        title="Ustawienia raportu wizualnego"
        title-class="font-18"
        size="md"
        hide-footer
    >
      <div v-if="currentSettings && currentSettings.length > 0">
        <div class="card shadow-sm mb-3">
          <div class="card-header bg-light">
            <h6 class="mb-0 text-primary fw-bold">
              <i class="fas fa-cog me-2"></i>Ustawienia widoku
            </h6>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div v-for="(setting, idx) in currentSettings" :key="idx" class="col-12">


                <div v-if="setting.visible === true">
                  <label class="form-label fw-semibold small">{{ setting.name || setting.key }}</label>
                  <div v-if="isBooleanSetting(setting)">
                    <div class="form-check form-switch">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          :id="'setting-' + idx"
                          v-model="setting.value"
                      />
                      <label class="form-check-label" :for="'setting-' + idx">
                        {{ setting.value ? 'Tak' : 'Nie' }}
                      </label>
                    </div>
                  </div>
                  <div v-else-if="isNumberSetting(setting)">
                    <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="setting.value"
                    />
                  </div>
                  <div v-else-if="isDateSetting(setting)">
                    <input
                        :type="isDatetimeSetting(setting) ? 'datetime-local' : 'date'"
                        class="form-control form-control-sm"
                        v-model="setting.value"
                    />
                  </div>
                  <div v-else>
                    <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="setting.value"
                        :placeholder="setting.description || ''"
                    />
                  </div>
                  <div v-if="setting.description" class="form-text text-muted small">{{ setting.description }}</div>
                </div>


              </div>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-secondary btn-sm" @click="showSettingsModal = false">
            <i class="fas fa-times me-1"></i>Anuluj
          </button>
          <button class="btn btn-primary btn-sm" @click="saveSettings" :disabled="savingSettings">
            <span v-if="savingSettings"><i class="fas fa-spinner fa-spin me-1"></i>Zapisywanie...</span>
            <span v-else><i class="fas fa-save me-1"></i>Zapisz ustawienia</span>
          </button>
        </div>
      </div>
      <div v-else class="text-muted text-center py-4">
        <i class="fas fa-info-circle me-2"></i>Brak dostępnych ustawień dla tego raportu.
      </div>
    </b-modal>

    <!-- Chart Form Modal -->
    <VisualReportChartFormModal
        v-if="showChartModal"
        :showModal="showChartModal"
        :reportGuid="reportGuid"
        :reportType="viewConfig ? viewConfig.reportType : null"
        :editChart="chartModalEditTarget"
        :availableColumns="viewConfig ? viewConfig.columns : []"
        :aggregationMetadata="aggregationMetadata"
        @update:showModal="showChartModal = $event"
        @onSave="onChartSaved"
        @onClose="showChartModal = false"
    />

  </div>
</template>

<script>
import {BModal} from "bootstrap-vue-next";
import CommonTable2 from "@/components/widgets/CommonTable2.vue";
import CommonTableFilterSection2 from "@/components/widgets/CommonTableFilterSection2.vue";
import SearchBar2 from "@/components/widgets/SearchBar2.vue";
import {log} from "@/state/pinia/log";
import {useVisualReportsModule} from "@/state/pinia/visualReportsModule";
import {useLazyLoaderModule} from "@/state/pinia/lazyloaderManagerModule";
import {useSimplepharmAuthModule} from "@/state/pinia";
import Swal from "sweetalert2";
import VisualReportChartPanel from "@/views/ReportsManager/VisualReportChartPanel.vue";
import VisualReportChartFormModal from "@/views/ReportsManager/VisualReportChartFormModal.vue";

export default {
  name: "VisualReportVisualizationView",
  components: {
    BModal,
    CommonTable2,
    CommonTableFilterSection2,
    SearchBar2,
    VisualReportChartPanel,
    VisualReportChartFormModal,
  },
  props: {
    initialReportGuid: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      reportGuid: null,
      viewConfig: null,
      reportMetadata: null,
      loadingConfig: false,
      loadingRows: false,
      savingFilters: false,
      savingSettings: false,

      // Table data
      tableData: {},
      currentPage: 1,
      sortingKey: null,
      sortingDirection: "ASC",
      refreshTable: true,
      isTableVisible: true,

      // Filters and search
      selectedFilters: [],
      searchRequest: null,
      savedFiltersApplied: false,

      // Settings modal
      showSettingsModal: false,
      currentSettings: [],
      initiallyVisibleColumns: [],

      // Chart state (structured as single item; can be extended to array later)
      currentChart: null,
      currentChartData: null,
      loadingChart: false,
      chartHidden: false,

      // Chart modal
      showChartModal: false,
      chartModalEditTarget: null,
      // chart categories pagination
      chartPageNumber: 0,
      chartTotalPages: 0,
      chartTotalCategories: 0,
      chartHasNext: false,
      chartHasPrevious: false,
      chartCategoriesPaginated: false,

      // Aggregation state
      aggregationEnabled: false,
      aggregationMetadata: null,
      selectedGroupByKeys: [],
      selectedMeasures: [],
      selectedPostAggregationFilters: [],
    };
  },
  computed: {
    visualReportsModule() {
      return useVisualReportsModule();
    },
    lazyloaderModule() {
      return useLazyLoaderModule();
    },
    simplepharmAuth() {
      return useSimplepharmAuthModule();
    },
    searchableFields() {
      if (!this.viewConfig || !this.viewConfig.searchableFields) return [];
      return this.viewConfig.searchableFields.map((field) => ({
        key: typeof field === "string" ? field : field.key,
        name: typeof field === "string" ? field : (field.name || field.key),
      }));
    },
    filtersFetchMethod() {
      const reportType = this.viewConfig.reportType;
      const module = this.visualReportsModule;
      return (params) => {
        return module.httpGetVisualReportTypeFilters(reportType, params).then((response) => {
          return response;
        });
      };
    },
    pageSize() {
      if (this.currentSettings && this.currentSettings.length > 0) {
        const pageSizeSetting = this.currentSettings.find(s => s.settingKey === "page_size");
        console.log("PageSize setting:", pageSizeSetting);
        return pageSizeSetting && pageSizeSetting.value ? parseInt(pageSizeSetting.value) : 20;
      } else {
        return 20;
      }
    },
    isChartPanelVisible() {
      return !!(this.currentChart && !this.chartHidden);
    },

    hasAggregationMetadata() {
      return !!(
          this.aggregationMetadata &&
          (
              (this.aggregationMetadata.groupableColumns && this.aggregationMetadata.groupableColumns.length > 0) ||
              (this.aggregationMetadata.measures && this.aggregationMetadata.measures.length > 0)
          )
      );
    },

    aggregatedTableHeaders() {
      if (!this.aggregationMetadata) return [];
      const headers = [];
      (this.selectedGroupByKeys || []).forEach((key) => {
        const col = (this.aggregationMetadata.groupableColumns || []).find((c) => c.key === key);
        headers.push({
          text: col ? col.label : key,
          key: key,
          type: col && col.type ? col.type.toLowerCase() : "text",
          sortable: true,
          isEpochMillis: col ? col.epochMilliseconds : false,
          width: "150px",
        });
      });
      (this.selectedMeasures || []).forEach((m) => {
        const spec = (this.aggregationMetadata.measures || []).find((s) => s.key === m.columnKey);
        const alias = m.alias || (spec ? spec.defaultAlias : null) || m.columnKey;
        headers.push({
          text: spec ? (m.alias || spec.label) : alias,
          key: alias,
          type: "number",
          sortable: spec ? spec.sortable !== false : true,
          width: "150px",
        });

        this.initiallyVisibleColumns.push(alias);

      });
      return headers;
    },

  },
  methods: {

    goBackToList() {
      this.$router.push({name: "VisualReportsStage"});
    },
    toggleTableVisibility() {
      this.isTableVisible = !this.isTableVisible;
    },
    sort(sortKey) {
      log.log("sort:", sortKey);
      this.sortingKey = sortKey;
      this.toggleSortingDirection();
      this.reloadData();
    },
    toggleSortingDirection() {
      if (this.sortingDirection === "ASC") {
        this.sortingDirection = "DESC";
      } else {
        this.sortingDirection = "ASC";
      }
    },

    onSearchRequest(searchPayload) {
      log.log("onSearchRequest:", searchPayload);
      this.searchRequest = searchPayload;
      this.currentPage = 1;
      this.reloadData();
    },

    onFiltersChanged(filters) {

      this.savingFilters = true;
      this.selectedFilters = filters;
      console.log("Saving filters:", filters);
      this.visualReportsModule.httpSaveVisualReportFilters(this.reportGuid, filters).then(() => {
        this.savedFiltersApplied = true;
        log.log("onFiltersChanged:", filters);
        this.currentPage = 1;
        this.chartPageNumber = 0;
        this.reloadData();
        if (this.currentChart) {
          this.loadChartData();
        }
      });

    },

    saveVisibleColumns(columns) {
      const visibleColumnsSetting = this.currentSettings.find(s => s.settingKey === "visible_columns");
      if (visibleColumnsSetting) {

        // parse columns into comma separated string
        visibleColumnsSetting.value = columns.map(c => c.key).join(",");
      } else {
        this.currentSettings.push({
          settingKey: "visible_columns",
          value: columns,
        });
      }
      this.saveSettings();
    },

    buildRowsRequestBody() {
      const search = (this.searchRequest && this.searchRequest.searchPhrase && this.searchRequest.searchKey)
          ? {searchPhrase: this.searchRequest.searchPhrase, searchKey: this.searchRequest.searchKey}
          : null;
      const filters = (this.selectedFilters && this.selectedFilters.length > 0) ? this.selectedFilters : null;

      const groupByKeys = (this.selectedGroupByKeys && this.selectedGroupByKeys.length > 0)
          ? this.selectedGroupByKeys
          : null;
      const measures = (this.selectedMeasures && this.selectedMeasures.length > 0)
          ? this.selectedMeasures.map((m) => ({
            columnKey: m.columnKey,
            function: m.function,
            alias: m.alias || null,
            sortable: m.sortable !== undefined ? m.sortable : null,
            havingFilterable: m.havingFilterable !== undefined ? m.havingFilterable : null,
          }))
          : null;
      const postAggregationFilters = (this.selectedPostAggregationFilters && this.selectedPostAggregationFilters.length > 0)
          ? this.selectedPostAggregationFilters
          : null;

      return {
        search,
        filters,
        groupByKeys,
        measures,
        postAggregationFilters,
      };
    },

    loadAggregationMetadata() {
      if (!this.viewConfig || !this.viewConfig.reportType) return;
      this.visualReportsModule.httpGetVisualReportAggregationMetadata(this.viewConfig.reportType).then((response) => {
        if (response && response.status === 200) {
          this.aggregationMetadata = this.visualReportsModule.getVisualReportAggregationMetadata;
          this.applyAggregationSettings(this.currentSettings);
        }
      });
    },

    buildAggregationSettingsEntries() {
      const entries = [];

      const groupByValue = (this.selectedGroupByKeys && this.selectedGroupByKeys.length > 0)
          ? this.selectedGroupByKeys.join(",")
          : null;
      entries.push({
        id: null,
        settingKey: 'aggregation_group_by_keys',
        name: 'Aggregation Group By Keys',
        value: groupByValue,
        valueType: 'COLLECTION',
        requireNotEmpty: false,
        visible: false,
        local: false,
        createdAt: null,
        updatedAt: null,
      });
      console.log("GroupByValue:", groupByValue);

      const measuresValue = (this.selectedMeasures && this.selectedMeasures.length > 0)
          ? JSON.stringify(this.selectedMeasures)
          : null;
      entries.push({
        id: null,
        settingKey: 'aggregation_measures',
        name: 'Aggregation Measures',
        value: measuresValue,
        valueType: 'STRING',
        requireNotEmpty: false,
        visible: false,
        local: false,
        createdAt: null,
        updatedAt: null,
      });

      const postFiltersValue = (this.selectedPostAggregationFilters && this.selectedPostAggregationFilters.length > 0)
          ? JSON.stringify(this.selectedPostAggregationFilters)
          : null;
      entries.push({
        id: null,
        settingKey: 'aggregation_post_filters',
        name: 'Aggregation Post Filters',
        value: postFiltersValue,
        valueType: 'STRING',
        requireNotEmpty: false,
        visible: false,
        local: false,
        createdAt: null,
        updatedAt: null,
      });

      return entries;
    },

    applyAggregationSettings(settings) {
      if (!settings || settings.length === 0) return;

      try {
        const groupBySetting = settings.find(s => s.settingKey === 'aggregation_group_by_keys');
        console.log("GroupBySetting:", groupBySetting);
        if (groupBySetting && groupBySetting.value) {
          const parsed = groupBySetting.value.split(",").map(k => k.trim()).filter(k => k !== "");
          if (Array.isArray(parsed)) {
            const validKeys = this.aggregationMetadata
                ? (this.aggregationMetadata.groupableColumns || []).map(c => c.key)
                : null;
            this.selectedGroupByKeys = validKeys
                ? parsed.filter(k => validKeys.includes(k))
                : parsed;
          }
          console.log("Selected group by keys:", this.selectedGroupByKeys);
        }
      } catch (e) {
        console.warn('Failed to restore aggregation_group_by_keys:', e);
        this.selectedGroupByKeys = [];
      }

      try {
        const measuresSetting = settings.find(s => s.settingKey === 'aggregation_measures');
        if (measuresSetting && measuresSetting.value) {
          const parsed = JSON.parse(measuresSetting.value);
          if (Array.isArray(parsed)) {
            const validKeys = this.aggregationMetadata
                ? (this.aggregationMetadata.measures || []).map(m => m.key)
                : null;
            this.selectedMeasures = validKeys
                ? parsed.filter(m => m.columnKey && validKeys.includes(m.columnKey))
                : parsed;
          }
        }
      } catch (e) {
        console.warn('Failed to restore aggregation_measures:', e);
        this.selectedMeasures = [];
      }

      try {
        const postFiltersSetting = settings.find(s => s.settingKey === 'aggregation_post_filters');
        if (postFiltersSetting && postFiltersSetting.value) {
          const parsed = JSON.parse(postFiltersSetting.value);
          if (Array.isArray(parsed)) {
            this.selectedPostAggregationFilters = parsed;
          }
        }
      } catch (e) {
        console.warn('Failed to restore aggregation_post_filters:', e);
        this.selectedPostAggregationFilters = [];
      }

      if (this.selectedGroupByKeys.length > 0 || this.selectedMeasures.length > 0) {
        this.aggregationEnabled = true;
      }
    },

    enableAggregation() {
      this.aggregationEnabled = true;
      this.currentPage = 1;
      this.reloadData();
    },

    disableAggregation() {
      this.aggregationEnabled = false;
      this.currentPage = 1;
      this.reloadData();
    },

    addMeasure() {
      if (!this.aggregationMetadata || !this.aggregationMetadata.measures || this.aggregationMetadata.measures.length === 0) return;
      const spec = this.aggregationMetadata.measures[0];
      this.selectedMeasures.push({
        columnKey: spec.key,
        function: spec.defaultFunction || (spec.allowedFunctions && spec.allowedFunctions[0]) || "COUNT",
        alias: spec.defaultAlias || "",
        sortable: spec.sortable !== false,
        havingFilterable: spec.havingFilterable === true,
      });
      this.saveSettings();
    },

    removeMeasure(idx) {
      this.selectedMeasures.splice(idx, 1);
      this.saveSettings();
    },

    onMeasureKeyChange(idx) {
      const m = this.selectedMeasures[idx];
      if (!m) return;
      const spec = (this.aggregationMetadata.measures || []).find((s) => s.key === m.columnKey);
      if (spec) {
        m.function = spec.defaultFunction || (spec.allowedFunctions && spec.allowedFunctions[0]) || "COUNT";
        m.alias = spec.defaultAlias || "";
        m.sortable = spec.sortable !== false;
        m.havingFilterable = spec.havingFilterable === true;
      }
      this.saveSettings();
    },

    onMeasureFunctionChange(){
      this.saveSettings();
    },


    buildTableHeaders() {
      if (!this.viewConfig || !this.viewConfig.columns) return [];
      var visibleColumnsSetting = this.currentSettings.find(s => s.settingKey === "visible_columns")

      var columnsVisible = this.viewConfig.columns.map((col) => ({
        text: col.text || col.key,
        key: col.key,
        type: col.type ? col.type.toLowerCase() : "text",
        sortable: col.sortable !== false,
        isEpochMillis: col ? col.epochMilliseconds : false,
        width: col.width || "150px",
      }));

      if (visibleColumnsSetting && visibleColumnsSetting.value && visibleColumnsSetting.value.length > 0) {
        const visibleColumnsSettingArray = visibleColumnsSetting.value.toString().split(",").map(col => col.trim());
        this.initiallyVisibleColumns = columnsVisible.filter(col => visibleColumnsSettingArray.includes(col.key)).map(col => col.key);
      }

      return columnsVisible;
    },

    buildTableData() {
      const rows = JSON.parse(JSON.stringify(this.visualReportsModule.getVisualReportRows));
      const indexedRows = rows.map((row, idx) => Object.assign({__vr_row_idx: idx}, row));
      const headers = (this.aggregationEnabled && this.aggregatedTableHeaders.length > 0)
          ? this.aggregatedTableHeaders
          : this.buildTableHeaders();
      this.tableData = {
        headers,
        massActions: [],
        data: indexedRows,
      };
    },

    loadViewConfig() {
      this.loadingConfig = true;
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Ładowanie konfiguracji raportu...");

      this.visualReportsModule.httpGetVisualReportViewConfig(this.reportGuid, {}).then((response) => {
        try {
          if (response && response.status === 200) {
            this.viewConfig = this.visualReportsModule.getVisualReportViewConfig;

            this.sortingKey = this.viewConfig.defaultSortColumn || null;
            this.sortingDirection = this.viewConfig.defaultSortDirection || "ASC";

            // Load saved settings into editor
            if (this.viewConfig.settings && this.viewConfig.settings.length > 0) {
              this.currentSettings = JSON.parse(JSON.stringify(this.viewConfig.settings));
              console.log("Loaded saved settings:", this.currentSettings);
            } else if (this.viewConfig.availableSettings && this.viewConfig.availableSettings.length > 0) {
              this.currentSettings = JSON.parse(JSON.stringify(this.viewConfig.availableSettings));
            }

            // Apply saved filters if present
            if (this.viewConfig.savedFilters && this.viewConfig.savedFilters.length > 0) {
              this.selectedFilters = JSON.parse(JSON.stringify(this.viewConfig.savedFilters));
              this.savedFiltersApplied = true;
            }
            this.visualReportsModule.httpGetVisualReportTypeMetadata(this.viewConfig.reportType, {}).then((response) => {
              if (response.status === 200) {
                this.reportMetadata = response.data.payload;
              }
            });
            this.loadAggregationMetadata();
          }
          this.reloadData();
          this.loadChart();
        } finally {
          this.loadingConfig = false;
          this.lazyloaderModule.setDisplayLazyLoader(false);
          this.lazyloaderModule.setProgressBarMessage("");
        }
      });


    },

    reloadData() {
      if (!this.reportGuid) return;
      this.loadingRows = true;
      this.refreshTable = false;
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Pobieranie danych raportu...");

      this.visualReportsModule.httpGetVisualReportRows(this.reportGuid, {
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        sortingKey: this.sortingKey,
        sortingDirection: this.sortingDirection,
        body: this.buildRowsRequestBody(),
      }).then(() => {
        try {
          this.buildTableData();
          this.refreshTable = true;
        } finally {
          this.loadingRows = false;
          this.lazyloaderModule.setDisplayLazyLoader(false);
          this.lazyloaderModule.setProgressBarMessage("");
        }
      });
    },

    openSettingsModal() {
      this.showSettingsModal = true;
    },

    saveGroupedColumns(event) {
     const column_key_clicked = event.target.value
      if (event.target.checked) {
        if (!this.selectedGroupByKeys.includes(column_key_clicked)) {
          this.selectedGroupByKeys.push(column_key_clicked);
        }
      } else {
        this.selectedGroupByKeys = this.selectedGroupByKeys.filter(k => k !== column_key_clicked);
      }
      this.saveSettings();
    },

    async saveSettings() {
      this.savingSettings = true;
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Zapisywanie ustawień...");
      try {
        const aggregationKeys = ['aggregation_group_by_keys', 'aggregation_measures', 'aggregation_post_filters'];
        const baseSettings = this.currentSettings.filter(s => !aggregationKeys.includes(s.settingKey));
        const settingsToSave = baseSettings.concat(this.buildAggregationSettingsEntries());
        console.log("Saving settings:", settingsToSave);
        const response = await this.visualReportsModule.httpSaveVisualReportSettings(this.reportGuid, settingsToSave);
        if (response && response.status === 200) {
          this.showSettingsModal = false;
          await this.reloadData();
        }
      } finally {
        this.savingSettings = false;
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.lazyloaderModule.setProgressBarMessage("");
      }
    },

    // Setting type helpers
    isBooleanSetting(setting) {
      return (setting.type || "").toUpperCase() === "BOOLEAN";
    },
    isNumberSetting(setting) {
      const t = (setting.type || "").toUpperCase();
      return t === "NUMBER" || t === "INTEGER" || t === "DECIMAL";
    },
    isDateSetting(setting) {
      const t = (setting.type || "").toUpperCase();
      return t === "DATE" || t === "DATETIME";
    },
    isDatetimeSetting(setting) {
      return (setting.type || "").toUpperCase() === "DATETIME";
    },

    // ======================= Chart methods =======================

    normalizeChartConfig(config) {
      if (!config) return null;
      return Object.assign({}, config, {
        chartType: (config.chartType || "bar").toLowerCase(),
        visible: config.enabled !== false,
        xAxisKey: config.xAxisColumn || "",
        yAxisColumn: config.yAxisColumn || "",
        seriesKeys: config.seriesColumns || [],
        aggregationType: config.aggregationType || null,
      });
    },

    denormalizeChartConfig(normalizedConfig) {
      if (!normalizedConfig) return null;
      return Object.assign({}, normalizedConfig, {
        chartType: (normalizedConfig.chartType || "bar").toUpperCase(),
        xAxisColumn: normalizedConfig.xAxisColumn || normalizedConfig.xAxisKey || "",
        seriesColumns: normalizedConfig.seriesColumns || normalizedConfig.seriesKeys || [],
        enabled: normalizedConfig.enabled !== undefined ? normalizedConfig.enabled : normalizedConfig.visible !== false,
      });
    },

    loadChart() {
      if (!this.reportGuid) return;
      this.loadingChart = true;
      this.visualReportsModule.httpGetVisualReportChartConfig(this.reportGuid).then((response) => {
        if (response && response.status === 200) {
          const rawConfig = response.data.payload;
          if (rawConfig && rawConfig.enabled !== false) {
            this.currentChart = this.normalizeChartConfig(rawConfig);
            this.chartPageNumber = 0;
            this.loadChartData();
          } else {
            this.currentChart = null;
            this.currentChartData = null;
            this.chartPageNumber = 0;
            this.chartTotalPages = 0;
            this.chartTotalCategories = 0;
            this.chartHasNext = false;
            this.chartHasPrevious = false;
            this.chartCategoriesPaginated = false;
          }
        }
      }).finally(() => {
        this.loadingChart = false;
      });
    },

    loadChartData() {
      if (!this.currentChart) return;

      this.loadingChart = true;

      this.visualReportsModule.httpGetVisualReportChartData(
          this.reportGuid,
          this.selectedFilters || [],
          this.chartPageNumber,
          this.pageSize
      ).then((response) => {
        if (response && response.status === 200) {
          const payload = response.data.payload || null;

          this.currentChartData = payload;
          console.log("Chart data:", this.currentChartData);

          this.chartTotalPages = payload?.totalPages || 0;
          this.chartTotalCategories = payload?.totalCategories || 0;
          this.chartHasNext = payload?.hasNext || false;
          this.chartHasPrevious = payload?.hasPrevious || false;
          this.chartCategoriesPaginated = payload?.categoriesPaginated || false;
        }
      }).finally(() => {
        this.loadingChart = false;
      });
    },

    changeChartPage(pageNumber) {
      if (pageNumber < 0) return;
      if (this.chartTotalPages > 0 && pageNumber >= this.chartTotalPages) return;

      this.chartPageNumber = pageNumber;
      this.loadChartData();
    },

    getChartPageNumbers() {
      const total = this.chartTotalPages || 0;
      const current = this.chartPageNumber || 0;

      if (total <= 1) {
        return [0];
      }

      const pages = [];
      const start = Math.max(0, current - 2);
      const end = Math.min(total - 1, current + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    refreshChart() {
      if (!this.currentChart) return;
      this.chartPageNumber = 0;
      this.loadChartData();
    },

    openCreateChartModal() {
      this.chartModalEditTarget = null;
      this.showChartModal = true;
    },

    openEditChartModal() {
      this.chartModalEditTarget = this.currentChart;
      this.showChartModal = true;
    },

    onChartSaved(savedChart) {
      log.log("onChartSaved:", savedChart);
      this.currentChart = this.normalizeChartConfig(savedChart);
      this.chartHidden = false;
      this.chartPageNumber = 0;
      if (this.currentChart) {
        this.loadChartData();
      }
    },

    toggleChartVisibility() {
      this.chartHidden = !this.chartHidden;
    },

    deleteChart() {
      if (!this.currentChart) return;
      Swal.fire({
        title: "Usuń wykres",
        text: "Czy na pewno chcesz usunąć wykres?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Usuń",
        cancelButtonText: "Anuluj",
        customClass: {container: "swal-highest"},
      }).then((result) => {
        if (result.isConfirmed) {
          const disabledPayload = Object.assign({}, this.denormalizeChartConfig(this.currentChart), {
            enabled: false,
          });
          this.visualReportsModule.httpSaveVisualReportChartConfig(this.reportGuid, disabledPayload).then((response) => {
            if (response && response.status === 200) {
              this.currentChart = null;
              this.currentChartData = null;
              this.chartHidden = false;
            }
          });
        }
      });
    },
  },
  watch: {
    currentPage: {
      handler() {
        this.reloadData();
      },
      deep: true,
    },
    selectedGroupByKeys: {
      handler() {
        this.currentPage = 1;
        this.chartPageNumber = 0;
        this.reloadData();
      },
      deep: true,
    },
    selectedMeasures: {
      handler() {
        this.currentPage = 1;
        this.chartPageNumber = 0;

        this.reloadData();
      },
      deep: true,
    },
  },
  async created() {
    if (this.initialReportGuid && this.initialReportGuid.length > 0) {
      console.log("Using initial report guid:", this.initialReportGuid);
      this.reportGuid = this.initialReportGuid;
    } else {
      console.log("Using route guid:", this.$route.params.guid);
      this.reportGuid = this.$route.params.guid;
    }
    log.log("VisualReportVisualizationView created, guid:", this.reportGuid);

    if (!this.reportGuid) {
      Swal.fire({
        title: "Błąd",
        text: "Brak identyfikatora raportu wizualnego.",
        icon: "error",
        customClass: {container: "swal-highest"},
      });
      this.goBackToList();
      return;
    }

    this.simplepharmAuth.refreshToken().then(() => {
      this.loadViewConfig();
    });


  },
};
</script>

<style scoped>
.visual-report-visualization {
  margin-left: 8px;
  margin-right: 8px;
}
</style>
